// eslint-disable-next-line no-redeclare, @typescript-eslint/no-redeclare
import { Group, Box, Stack, Text, Flex } from '@mantine/core';
import { IconDragDrop, IconDragDrop2 } from '@tabler/icons';
import { Dispatch, FC, useContext } from 'react';
import Moment from 'react-moment';
import { useNavigate } from 'react-router-dom';
import { UseListStateHandlers } from '@mantine/hooks';
import { useDrag, useDrop } from 'react-dnd-latest';
import { SpanDot } from '../../../Atoms/Dot';
import { WorkflowTemplateListViewDTO } from '../../../Types/LogT';
import { WorkflowS } from '../../../Service/WorkflowS';
import { WindowContext } from '../../../Service/Context/WindowContext';
import WorkflowTemplateActions from './WorkflowTemplateActions';
import WorkflowTemplateTriggerDescription from './WorkflowTemplateTriggerDescription';

interface Props {
  template: WorkflowTemplateListViewDTO;
  handlers: UseListStateHandlers<WorkflowTemplateListViewDTO>;
  showArchived: boolean;
  groups: string[];
  setTab: Dispatch<string>;
}

const WorkflowTemplateListItem: FC<Props> = ({ template, handlers, showArchived, groups, setTab }) => {
  const navigate = useNavigate();
  const { lg } = useContext(WindowContext);
  const { id: templateId, enabled, name, lastModified, archived } = template;
  const { setState: setTemplates, filter, applyWhere } = handlers;

  const [{ isOver, canDrop }, drop] = useDrop(
    () => ({
      accept: 'workflowTemplate',
      canDrop: (item: WorkflowTemplateListViewDTO) => item.id !== templateId,
      collect: (monitor) => ({
        isOver: monitor.isOver() && monitor.getItem().id !== templateId,
        canDrop: !!monitor.canDrop(),
      }),
      drop: async (item: any) => {
        const list = await WorkflowS.moveTemplate(item.id, templateId);
        if (list) {
          setTemplates(list);
        }
      },
    }),
    [templateId],
  );

  const [{ isDragging }, drag] = useDrag(
    () => ({
      type: 'workflowTemplate',
      item: template,
      collect: (monitor) => ({
        isDragging: monitor.isDragging(),
        handlerId: monitor.getHandlerId(),
      }),
    }),
    [templateId],
  );

  if (archived && !showArchived) {
    return null;
  }

  const handleChange = (id: string, patch: Partial<WorkflowTemplateListViewDTO>) =>
    applyWhere(
      (item) => item.id === id,
      (item) => ({ ...item, ...patch }),
    );

  const remove = (id: string) => filter((t) => t.id !== id);

  return (
    <div style={{ opacity: isDragging ? 0.5 : 1 }}>
      <Flex gap="lg" align="center">
        {lg && (
          <div ref={drop} style={{ width: 30 }}>
            <div ref={drag}>
              {canDrop && <IconDragDrop color="#1D5191" size={isOver ? 28 : 20} />}
              {!canDrop && <IconDragDrop2 size={20} style={{ cursor: 'grab' }} />}
            </div>
          </div>
        )}
        <Box sx={{ flexGrow: 1 }}>
          <Group noWrap position="apart">
            <Stack spacing={0}>
              <Text
                size="md"
                className="underline-hover"
                strikethrough={archived}
                onClick={() => navigate(`/settings/workflow/templates/${templateId}`)}
              >
                <SpanDot withoutShadow size={8} colour={enabled ? '#4EBF46' : '#686868'} styles={{ marginRight: 10 }} />
                {name}
              </Text>
              <WorkflowTemplateTriggerDescription template={template} />
              <Text size="xs" color="dimmed">
                Bearbeitet{'  '}
                <Moment fromNow date={lastModified} />
              </Text>
            </Stack>
            <div>
              <WorkflowTemplateActions
                template={template}
                handleChange={handleChange}
                remove={() => remove(templateId)}
                groups={groups}
                setTab={setTab}
              />
            </div>
          </Group>
        </Box>
      </Flex>
    </div>
  );
};

export default WorkflowTemplateListItem;
